import React, { useState } from 'react';
import { BriefcaseIcon, Loader2Icon } from 'lucide-react';
import { useInterviewStore } from '../store';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export function JobDescriptionForm() {
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const setJobDescription = useInterviewStore((state) => state.setJobDescription);
  const setCurrentInterview = useInterviewStore((state) => state.setCurrentInterview);
  const interviewConfig = useInterviewStore((state) => state.interviewConfig);
  const { t, i18n } = useTranslation();

  const generateManualJobDescription = (title: string, language: string) => {
    const isSpanish = language === 'es';

    if (!title.trim()) return null;

    if (title.toLowerCase().includes('software') || title.toLowerCase().includes('developer')) {
      return {
        title,
        description: isSpanish
          ? `Buscamos un ${title} apasionado para unirse a nuestro equipo de desarrollo. El candidato ideal combinará excelentes habilidades técnicas con una mentalidad colaborativa y orientada a resultados.`
          : `We are seeking a passionate ${title} to join our development team. The ideal candidate will combine excellent technical skills with a collaborative and results-driven mindset.`,
        requirements: isSpanish
          ? [
              "Experiencia demostrable en desarrollo de software",
              "Sólidos conocimientos en arquitectura de software",
              "Excelentes habilidades de comunicación",
              "Capacidad para trabajar en equipo"
            ]
          : [
              "Proven software development experience",
              "Strong understanding of software architecture",
              "Excellent communication skills",
              "Ability to work well in a team"
            ],
        responsibilities: isSpanish
          ? [
              "Desarrollar y mantener aplicaciones de alta calidad",
              "Colaborar con equipos multidisciplinarios",
              "Participar en revisiones de código",
              "Mentorear a desarrolladores junior"
            ]
          : [
              "Develop and maintain high-quality applications",
              "Collaborate with cross-functional teams",
              "Participate in code reviews",
              "Mentor junior developers"
            ],
        yearsOfExperience: interviewConfig.yearsOfExperience,
        salaryRange: interviewConfig.salaryRange,
        turnoverRate: interviewConfig.turnoverRate
      };
    }

    // Default job description template
    return {
      title,
      description: isSpanish
        ? `Estamos en búsqueda de un ${title} talentoso para fortalecer nuestro equipo. Valoramos la iniciativa, la creatividad y el compromiso con la excelencia.`
        : `We are looking for a talented ${title} to strengthen our team. We value initiative, creativity, and commitment to excellence.`,
      requirements: isSpanish
        ? [
            "Experiencia relevante en el campo",
            "Excelentes habilidades de comunicación",
            "Capacidad de trabajo en equipo",
            "Orientación a resultados"
          ]
        : [
            "Relevant field experience",
            "Excellent communication skills",
            "Team player mindset",
            "Results-oriented approach"
          ],
      responsibilities: isSpanish
        ? [
            "Contribuir al éxito del equipo",
            "Implementar mejores prácticas",
            "Participar en reuniones estratégicas",
            "Trabajar en conjunto con otros departamentos"
          ]
        : [
            "Contribute to the team's success",
            "Implement best practices",
            "Participate in strategic meetings",
            "Work collaboratively across departments"
          ],
      yearsOfExperience: interviewConfig.yearsOfExperience,
      salaryRange: interviewConfig.salaryRange,
      turnoverRate: interviewConfig.turnoverRate
    };
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!title.trim()) {
      toast.error(t('jobDescription.titleRequired'));
      return;
    }

    const manualDescription = generateManualJobDescription(title, i18n.language);
    if (manualDescription) {
      setJobDescription(manualDescription);
      // Don't create an interview here - only set the job description
      setCurrentInterview(null);
      toast.success(t('jobDescription.success'));
    } else {
      toast.error(t('errors.jobDescription'));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t('jobDescription.title')}
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          placeholder={t('jobDescription.titlePlaceholder')}
        />
      </div>

      <div className="flex gap-4">
        <button
          type="submit"
          className="px-6 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 disabled:opacity-50"
        >
          {t('jobDescription.manualInput')}
        </button>
      </div>
    </form>
  );
}